import { FC, useEffect } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { useConnectPlaid } from '../plaid/hooks/useConnectPlaid';
import { ReactComponent as CoinOutline } from '@/assets/images/coin-outline.svg';
import { ReactComponent as Hand } from '@/assets/images/hand.svg';
import { ReactComponent as Shield } from '@/assets/images/shield.svg';
import { ReactComponent as Security } from '@/assets/images/security.svg';
import LoadingButton from '@mui/lab/LoadingButton';
import BankIconImage from '@/assets/images/bank-icon.png';
import analytics from '@/utils/analytics/analyticsInstance';

const Container = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const Main = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  paddingInline: 16,
  maxWidth: 600,

  '@supports (width: -webkit-fill-available)': {
    width: '-webkit-fill-available',
  },

  '@supports (width: -moz-available)': {
    width: '-moz-available',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  minWidth: 48,
  borderWidth: 1,
  display: 'grid',
  placeItems: 'center',
  borderStyle: 'solid',
  borderRadius: '50%',
  borderColor: theme.palette.border.main,
}));

const Image = styled('img')(() => ({
  width: 140,
  height: 140,
}));

const Footer = styled(Box)(({ theme }) => ({
  padding: 16,
  borderTopWidth: 1,
  display: 'flex',
  justifyContent: 'center',
  borderTopStyle: 'solid',
  borderTopColor: theme.palette.border.main,

  '@supports (width: -webkit-fill-available)': {
    width: '-webkit-fill-available',
  },

  '@supports (width: -moz-available)': {
    width: '-moz-available',
  },
}));

const LIST = [
  {
    icon: CoinOutline,
    name: 'Connect your bank to get cashback on every purchase.',
  },
  {
    icon: Security,
    name: 'Your bank securely shares limited data with our trusted partner, Plaid.',
  },
  {
    icon: Hand,
    name: 'We can’t access your money — we just help you earn rewards.',
  },
  {
    icon: Shield,
    name: 'We never store your bank login. Your info stays private and secure.',
  },
];

const LinkBankStep: FC = () => {
  const { isLoading, onConnect } = useConnectPlaid();

  useEffect(() => {
    analytics.track('webOnboardingScreenOpened', {
      screen_title: 'Link bank account',
    });
  }, []);

  return (
    <Container>
      <Image src={BankIconImage} />
      <Main>
        <Typography
          fontSize={28}
          fontWeight={700}
          lineHeight="32px"
          textAlign="center"
          color="black"
        >
          Link bank account
        </Typography>
        {LIST.map((item, index) => {
          const Icon = item.icon;

          return (
            <Box key={index} gap={1.5} display="flex" alignItems="center">
              <IconWrapper>
                <Icon />
              </IconWrapper>
              <Typography fontSize={16} lineHeight="24px" color="black">
                {item.name}
              </Typography>
            </Box>
          );
        })}
      </Main>
      <Footer>
        <LoadingButton
          loading={isLoading}
          onClick={onConnect}
          variant="contained"
          size="large"
          sx={{ maxWidth: 600, width: '100%' }}
        >
          Link my account
        </LoadingButton>
      </Footer>
    </Container>
  );
};

export default LinkBankStep;
